import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import ChatButton from './ChatButton';
import ChatWindow from './ChatWindow';
import useChat from '../../hooks/useChat';
import './ChatWidget.css';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [chatPhase, setChatPhase] = useState('chat'); // 'chat' | 'confirm-close' | 'feedback' | 'thanks'
  const containerRef = useRef(null);

  // Bot chat hook
  const {
    messages: botMessages,
    isLoading: botLoading,
    error: botError,
    sessionId: botSessionId,
    selectionPath,
    startChat,
    selectOption,
    sendMessage: sendBotMessage,
    compareMode,
    selectedCards,
    toggleCardSelection,
    submitComparison,
    checklistMode,
    selectedCriteria,
    toggleCriteriaSelection,
    submitRecommendation,
    wizardMode,
    wizardSteps,
    currentStepIndex,
    wizardSelections,
    toggleWizardSelection,
    goToNextStep,
    goToPrevStep,
    submitWizard,
    refreshOptions,
    resetChat,
    latestOptionSetId,
  } = useChat();

  const messages = botMessages;
  const error = botError;
  const isLoading = botLoading;

  // Chat açıldığında başlat (müşteri bilgisi varsa)
  useEffect(() => {
    if (isOpen && botMessages.length === 0 && customerInfo) {
      startChat(customerInfo.name, customerInfo.surname, customerInfo.phone, customerInfo.kvkkAccepted, customerInfo.acikRizaAccepted);
    }
  }, [isOpen, botMessages.length, startChat, customerInfo]);

  // Iframe (embed) icindeysek, ac/kapa durumunu parent pencereye bildir.
  // public/embed.js bunu dinleyip iframe'i buyutur/kucultur.
  useEffect(() => {
    if (window.self === window.top) return; // iframe icinde degiliz, gerek yok
    window.parent.postMessage(
      { type: 'lidya-chat', open: isOpen },
      '*'
    );
  }, [isOpen]);

  // Chat aç/kapa
  const toggleChat = () => {
    setIsOpen((prev) => !prev);
  };

  // Müşteri bilgi formu submit
  const handleCustomerInfoSubmit = async (info) => {
    setCustomerInfo(info);
  };

  // X butonuna basıldığında
  const handleClose = () => {
    // Tesekkur ekranindayken tekrar onay sormayiz: kullanici zaten
    // degerlendirmesini gonderdi, X dogrudan kapatir.
    if (chatPhase === 'thanks') {
      performClose();
      return;
    }
    const hasSession = !!botSessionId;
    if (hasSession && customerInfo) {
      setChatPhase('confirm-close');
    } else {
      performClose();
    }
  };

  // Onay ekranında "Kapat" butonuna basıldığında
  const handleConfirmClose = () => {
    setChatPhase('feedback');
  };

  // Onay ekranında "İptal" butonuna basıldığında
  const handleCancelClose = () => {
    setChatPhase('chat');
  };

  // Feedback tamamlandığında.
  // Puan gonderildiyse widget KAPANMAZ — tesekkur mesaji pencerenin icinde
  // gosterilir ve kapatma kararini kullanici verir. Boylece embed edilmis
  // sitede kirpilmis bir toast gorunmez. Atlandiysa dogrudan kapatilir.
  const handleFeedbackComplete = (submitted) => {
    if (submitted) {
      setChatPhase('thanks');
    } else {
      performClose();
    }
  };

  // Widget'ı tamamen kapat ve sıfırla
  const performClose = () => {
    setIsOpen(false);
    setCustomerInfo(null);
    setChatPhase('chat');
    resetChat();
  };

  // Chat'i minimize et ("-" butonu ve dışarı tıklama aynı davranışı paylaşır:
  // oturum ve mesajlar korunur, sadece pencere kapanır)
  const handleMinimize = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Dışarı tıklayınca alta in. İki kaynak var:
  //  1) Widget'ın kendi dokümanı — demo sayfasında sayfanın geri kalanı,
  //     embed modunda ise iframe'in pencere dışındaki şeffaf kenarı.
  //  2) Host sayfa — iframe içindeki olaylar parent'a sıçramadığı için
  //     public/embed.js host dokümanındaki tıklamayı yakalayıp bize mesaj atar.
  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        handleMinimize();
      }
    };

    const handleParentMessage = (event) => {
      if (event.source !== window.parent) return;
      if (event.data?.type === 'lidya-chat-outside-click') {
        handleMinimize();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    window.addEventListener('message', handleParentMessage);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      window.removeEventListener('message', handleParentMessage);
    };
  }, [isOpen, handleMinimize]);

  // Seçenek seçildiğinde
  const handleSelectOption = async (option) => {
    if (option.useLlm) {
      const fullPath = [...selectionPath, option.displayText];
      const dynamicPrompt = fullPath.join(' - ') + ' hakkında detaylı bilgi ver';
      const displayText = `${option.emoji || ''} ${option.displayText}`.trim();
      await sendBotMessage(dynamicPrompt, displayText);
      return;
    }
    await selectOption(option);
  };

  return (
    <div className="chat-widget-container" ref={containerRef}>
      <AnimatePresence>
        {isOpen && (
          <ChatWindow
            messages={messages}
            isLoading={isLoading}
            error={error}
            onSelectOption={handleSelectOption}
            onClose={handleClose}
            onMinimize={handleMinimize}
            onSendBotMessage={sendBotMessage}
            customerInfo={customerInfo}
            onCustomerInfoSubmit={handleCustomerInfoSubmit}
            chatPhase={chatPhase}
            onConfirmClose={handleConfirmClose}
            onCancelClose={handleCancelClose}
            onFeedbackComplete={handleFeedbackComplete}
            sessionId={botSessionId}
            compareMode={compareMode}
            selectedCards={selectedCards}
            onCardToggle={toggleCardSelection}
            onCompare={submitComparison}
            checklistMode={checklistMode}
            selectedCriteria={selectedCriteria}
            onCriteriaToggle={toggleCriteriaSelection}
            onRecommend={submitRecommendation}
            wizardMode={wizardMode}
            wizardSteps={wizardSteps}
            currentStepIndex={currentStepIndex}
            wizardSelections={wizardSelections}
            onWizardToggle={toggleWizardSelection}
            onWizardNext={goToNextStep}
            onWizardPrev={goToPrevStep}
            onWizardSubmit={submitWizard}
            latestOptionSetId={latestOptionSetId}
          />
        )}
      </AnimatePresence>

      <div className="chat-button-wrapper">
        <ChatButton
          isOpen={isOpen}
          onClick={toggleChat}
        />
      </div>
    </div>
  );
};

export default ChatWidget;
